import { cn } from '@/lib/cn';
import { useSearchParams } from '@solidjs/router';
import type { PageMetaWithTotal } from '@uchat-team-management/shared';
import { For, type JSX, Show, createEffect, createSignal, on } from 'solid-js';
import { Button } from './Button';

interface PagedQuery<T> {
  isPending: boolean;
  data?: { pages: { data: T[]; meta: PageMetaWithTotal }[] };
  hasNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
}

/**
 * UCH-447: numbered pages over a keyset-cursor infinite query, with the page
 * number in the URL (`?page=N`, 1-based; page 1 omits the param). Cursors only
 * walk forward, so a not-yet-fetched target page is reached by fetching pages
 * in order. `filters` must be a memo that ignores the page param — its change
 * resets to page 1.
 */
export const createPagedList = <T,>(query: PagedQuery<T>, filters: () => unknown) => {
  const [params, setParams] = useSearchParams<{ page?: string }>();
  const page = () => Math.max(0, (Number.parseInt(params.page ?? '', 10) || 1) - 1);
  // setSearchParams is async, so mid-walk retargets are tracked in a signal the
  // walk loop reads each iteration instead of re-reading the (possibly stale) URL.
  const [target, setTarget] = createSignal(page());
  const [jumping, setJumping] = createSignal(false);

  const rows = () => query.data?.pages[page()]?.data ?? [];
  const meta = () => query.data?.pages[0]?.meta;
  const totalPages = () => Math.max(1, Math.ceil((meta()?.total ?? 0) / (meta()?.limit ?? 25)));

  const writePage = (p: number, replace = false) =>
    setParams({ page: p > 0 ? String(p + 1) : undefined }, { replace });

  const walk = async () => {
    if (jumping()) return;
    setJumping(true);
    try {
      while ((query.data?.pages.length ?? 0) <= target() && query.hasNextPage) {
        await query.fetchNextPage();
      }
    } finally {
      setJumping(false);
    }
    // The list may end before the target (stale deep link, shrunk data) — clamp.
    const last = Math.max(0, (query.data?.pages.length ?? 1) - 1);
    if (target() > last) {
      setTarget(last);
      writePage(last, true);
    }
  };

  const goToPage = (t: number) => {
    const clamped = Math.max(0, Math.min(t, totalPages() - 1));
    setTarget(clamped);
    writePage(clamped);
    void walk();
  };

  // A filter change starts a fresh infinite query — drop back to page 1.
  createEffect(
    on(
      filters,
      () => {
        setTarget(0);
        writePage(0, true);
      },
      { defer: true },
    ),
  );

  // Deep links and back/forward move the param without goToPage — fetch what's missing.
  createEffect(() => {
    const p = page();
    setTarget(p);
    if (!query.isPending && p >= (query.data?.pages.length ?? 0) && query.hasNextPage) void walk();
  });

  return { rows, page, totalPages, jumping, goToPage };
};

export interface PaginationProps {
  /** Current page, 0-based. */
  page: number;
  totalPages: number;
  onPage: (page: number) => void;
  /** Busy-spins the current page button and disables navigation while it loads. */
  loading?: boolean;
  class?: string;
}

// Hidden entirely when everything fits one page.
// ponytail: no page-window; add ellipsis when totalPages > ~10
export const Pagination = (props: PaginationProps): JSX.Element => (
  <Show when={props.totalPages > 1}>
    <nav class={cn('flex items-center justify-center gap-1', props.class)} aria-label="Pagination">
      <Button
        size="sm"
        variant="ghost"
        disabled={props.page === 0 || props.loading}
        onClick={() => props.onPage(props.page - 1)}
      >
        Prev
      </Button>
      <For each={Array.from({ length: props.totalPages }, (_, i) => i)}>
        {(i) => (
          <Button
            size="sm"
            variant={i === props.page ? 'secondary' : 'ghost'}
            aria-current={i === props.page ? 'page' : undefined}
            loading={props.loading && i === props.page}
            onClick={() => props.onPage(i)}
          >
            {i + 1}
          </Button>
        )}
      </For>
      <Button
        size="sm"
        variant="ghost"
        disabled={props.page >= props.totalPages - 1 || props.loading}
        onClick={() => props.onPage(props.page + 1)}
      >
        Next
      </Button>
    </nav>
  </Show>
);
